import React from 'react';
import { Row, Col, StatusTip } from 'tea-component';
import { DBO_TM } from '@op/utils/format';
import { DefaultImg } from '@op/constants/defaultImg';
import RenderImg from '@op/components/RenderImg';
import { useScrollBottomRegister } from '@opm/components/ScrollBottomList';
import ChannelItem from '../ChannelItem';
import CountriesList from '../CountriesList';
import MetricsBar from '../MetricsBar';
import CardCommon from '@op/components/CardCommon';
import { useTranslateConfig } from '@opm/hooks/useTranslateConfig';
import './index.scss';
import PlatformItem from '../PlatformItem';
import classNames from 'classnames';
import PlayableCover from '../PlayableCover';
import SvgIcon from '@home/components/SvgIcon';
import useIntls from '@opm/hooks/useIntls';
import { TOpenWindowsType } from '../type';

export interface CardListViewProps {
  data: any[];
  isFirstLoading?: boolean;
  isEmpty?: boolean;
  isPaginationLoading?: boolean;
  nextPage: () => void;
  open: (data: any, type?: TOpenWindowsType) => void;
  vertical?: boolean;
}

/**
 * 卡片列表视图组件 - 匹配 Figma 设计稿
 */
export default function CardListView({
  data,
  isFirstLoading,
  isEmpty,
  isPaginationLoading,
  nextPage,
  open,
  vertical = false,
}: CardListViewProps) {
  useScrollBottomRegister(nextPage);
  const { translateType } = useTranslateConfig();
  const { intl } = useIntls();

  return (
    <CardCommon
      loading={isFirstLoading}
      empty={isEmpty}
      emptyHeight={615}
      loadingHeight={615}
      contentCard
    >
      {vertical ? (
        <div className="dbom-cardview__grid--vertical">
          {data?.map((item: any) => (
            <div
              key={item.creative_id || item.app_name}
              className="dbom-cardview__grid-item"
            >
              <div className="dbom-cardview__card dbom-cardview__card--vertical">
                {/* 顶部封面区域 */}
                <div className="dbom-cardview__cover" onClick={() => open(item)}>
                  <PlayableCover
                    previewImg={item.preview_img}
                    type={item.type}
                    className="dbom-cardview__cover-img dbom-no-invert"
                  />
                  {/* 素材类型标签 */}
                  {item.type && item.type.length > 0 && (
                    <span className="dbom-cardview__type-tag">
                      {translateType?.(item.type[0]) || item.type[0]}
                    </span>
                  )}
                  {/* 底部信息栏：日期范围 + 天数 */}
                  <div className="dbom-cardview__cover-bottom">
                    <div className="dbom-cardview__app-row">
                      {item.app_logo && (
                        <RenderImg
                          coverImage={item.app_logo}
                          className="dbom-cardview__app-logo dbo-no-invert"
                          defaultImage={DefaultImg.KOL}
                        />
                      )}
                      <div className="dbom-cardview__app-detail">
                        <div
                          className="dbom-cardview__app-name"
                          title={item.app_name}
                        // onClick={() => open?.(item, 'ori')}
                        >
                          {item.app_name || '-'}
                        </div>
                        {
                          item?.title && <div className="dbom-cardview__body">{item.title}</div>
                        }
                      </div>
                    </div>
                    <div className="dbom-cardview__cover-meta">
                      {item.first_seen && item.last_seen && (
                        <span className="dbom-cardview__date-range">
                          {intl('Active Date')}{`: `}
                          {DBO_TM.timeTitle({
                            start_time: item.first_seen,
                            end_time: item.last_seen,
                            date_type: 'daily',
                          })}
                        </span>
                      )}
                      {item.video_duration > 0 && (
                        <span className="dbom-cardview__duration">
                          {DBO_TM.duration(item.video_duration)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="dbom-cardview__info">
                  <div className="dbom-cardview__meta-row">
                    <ChannelItem list={item.channel} />
                    <PlatformItem
                      list={item.platform}
                      className={classNames({
                        'dbom-line-divider': item?.countries?.length > 0,
                      })}
                    />
                    <CountriesList countries={item.countries} />
                  </div>
                  <MetricsBar records={item} variant="grid" />
                </div>

                {/* 鼠标滑过分享icon */}
                {item?.origin_url && (
                  <div className="dbom-cardview-share" onClick={(e) => {
                    e.stopPropagation();
                    open?.(item, 'ori');
                  }}>
                    <SvgIcon type="other-share-icon" />
                  </div>
                )}

              </div>
            </div>
          ))}
        </div>
      ) : (
        <Row>
          {data?.map((item: any) => (
            <Col key={item.creative_id || item.app_name} span={8}>
              <div className="dbom-cardview__card">
                {/* 顶部封面区域 */}
                <div className="dbom-cardview__cover" onClick={() => open(item)}>
                  <PlayableCover
                    previewImg={item.preview_img}
                    type={item.type}
                    className="dbom-cardview__cover-img dbom-no-invert"
                  />
                  {/* 素材类型标签 */}
                  {item.type && item.type.length > 0 && (
                    <span className="dbom-cardview__type-tag">
                      {translateType?.(item.type[0]) || item.type[0]}
                    </span>
                  )}
                  {/* 底部信息栏：日期范围 + 天数 */}
                  <div className="dbom-cardview__cover-bottom">
                    {item.first_seen && item.last_seen && (
                      <span className="dbom-cardview__date-range">
                        {DBO_TM.timeTitle({
                          start_time: item.first_seen,
                          end_time: item.last_seen,
                          date_type: 'daily',
                        })}
                      </span>
                    )}
                    {/* 视频时长角标 */}
                    {item.video_duration > 0 && (
                      <span className="dbom-cardview__duration">
                        {DBO_TM.duration(item.video_duration)}
                      </span>
                    )}
                  </div>
                </div>

                {/* 应用信息区域 */}
                <div className="dbom-cardview__info">
                  <div className="dbom-cardview__app-row">
                    {item.app_logo && (
                      <RenderImg
                        coverImage={item.app_logo}
                        className="dbom-cardview__app-logo  dbo-no-invert"
                        defaultImage={DefaultImg.KOL}
                      />
                    )}
                    <div className="dbom-cardview__app-detail">
                      <div
                        className="dbom-cardview__app-name"
                        title={item.app_name}
                        onClick={() => open?.(item, 'ori')}
                      >
                        {item.app_name || '-'}
                      </div>
                      <div className="dbom-cardview__meta-row">
                        {/* 渠道图标 */}
                        <ChannelItem list={item.channel} />
                        {/* 平台图标 */}
                        <PlatformItem
                          list={item.platform}
                          className={classNames({
                            'dbom-line-divider': item?.countries?.length > 0,
                          })}
                        />
                        {/* 国家图标 */}
                        <CountriesList countries={item.countries} />
                      </div>
                    </div>
                  </div>

                  {/* 正文描述 */}
                  <div className="dbom-cardview__body">{item?.title || '-'}</div>

                  {/* 底部指标 */}
                  <MetricsBar records={item} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      {isPaginationLoading && (
        <div className="loading-common dbom-loading-common">
          <StatusTip status="loading" />
        </div>
      )}
    </CardCommon>
  );
}
